import axios from 'axios'
import * as Sentry from '@/core/sentry'
import { http } from '@/utils/api'

export type JobResponse = {
  status: 'completed' | 'error' | 'queued' | 'running'
  result: string
  httpStatus?: number
}

const timeout = 60 * 60 * 1_000

function resolveJobUrl(jobUrl: string) {
  return jobUrl.replace(/^\/?api\//, '').replace(/^\/+/, '')
}

export async function loadJob<T>(
  jobUrl: string,
  onStatusChange?: (status: JobResponse['status']) => void,
  cancelToken?: any
) {
  try {
    const resolvedJobUrl = resolveJobUrl(jobUrl)

    const startTime = Date.now()

    let lastStatus: JobResponse['status'] | undefined = undefined

    while (Date.now() < startTime + timeout) {
      const { data } = await http.get<JobResponse>(resolvedJobUrl, {
        cancelToken,
      })

      // Notify callback of status changes
      if (lastStatus !== data.status) {
        lastStatus = data.status

        onStatusChange?.(data.status)
      }

      if (data.status === 'completed') {
        return {
          status: 'success' as const,
          payload: JSON.parse(data.result) as T,
        }
      } else if (data.status === 'error') {
        return {
          status: 'job-error' as const,
          payload: data,
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 2_500))
    }

    return {
      status: 'timeout-error' as const,
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
